import React from "react";
import { Link } from "react-router-dom";
import { isCollectorEnabled } from "../../../helpers/collectorSession";
import SiteLayout, { SUPPORT_WHATSAPP } from "./SiteLayout";
import { QrMark, Ripple, VerifyBox, Weave } from "./SiteBits";
import { TAG_PRICES_INR, formatUsd, useUsdRate } from "./usdRate";
import brocadeImg from "../assets/images/site/buyer-trust.jpg";
import loomImg from "../assets/images/site/front-page-1-3.jpg";
import collectorImg from "../assets/images/site/consumer.jpg";
import zariImg from "../assets/images/site/front-page-2-3.jpg";

const FACTS = [
  ["NFC + QR", "One tag per piece, bound to its certificate"],
  ["12 modules", "From loom floor to collector's shelf"],
  ["7 days", "Every Studio module free to try"],
  ["No app", "Buyers verify in the phone browser"],
  ["3 languages", "The app in English, Hindi and Telugu"],
];

const STAGES = [
  ["Make", "Loom work, attendance and wages logged daily on the floor.", "Karkhana"],
  ["Record", "Photos, process stages, materials and the maker's story.", "Studio · AI enrichment"],
  ["Certify & tag", "Issue a certificate and bind an NFC or pre-printed QR label.", "Certificates · QR labels"],
  ["Share & sell", "Private catalogues, reseller links, orders and partner production.", "Catalogue · Orders · Partners"],
  ["Verify & keep", "Buyers tap to verify, save it to My Collection, and get care guidance.", "My Collection · Care"],
];

const PIECES: [string, string, string, string, string][] = [
  ["Kadhua Banarasi", "Katan silk", "6/6", "NFC", "ok"],
  ["Jangla dupatta", "Organza", "4/6", "QR", "review"],
  ["Tanchoi stole", "Silk", "2/6", "—", "draft"],
  ["Rangkat saree", "Katan", "6/6", "NFC", "ok"],
];

/** Phone layout of the module grid — the bento tiles are too tall to stack. */
const MODULES_COMPACT: [string, string][] = [
  ["Core", "Products & certificates"],
  ["Tags", "NFC & QR labels"],
  ["Sell", "Catalogue"],
  ["Run", "Karkhana"],
  ["Sell", "Orders & partners"],
  ["Prove", "Geo, GI & looms"],
  ["Run", "Saree costing"],
  ["AI", "AI enrichment"],
  ["Keep", "My Collection"],
  ["Keep", "Care & preservation"],
];

const STATUS: Record<string, [string, string]> = {
  ok: ["s-pill--ok", "Certified"],
  review: ["s-pill--blue", "In review"],
  draft: ["s-pill--grey", "Draft"],
};

const HomePage = () => {
  const { inrPerUsd } = useUsdRate();
  const collectorOn = isCollectorEnabled();

  return (
    <SiteLayout title="HastKala Pramanak — proof for every handmade piece">
      <section className="s-hero">
        <Weave />
        <div className="s-wrap s-hero__grid">
          <div>
            <span className="s-eyebrow">Proof-of-craft infrastructure · India</span>
            <h1>
              Every handmade piece,
              <br />
              <span className="s-grad">provable.</span>
            </h1>
            <p className="s-lead">
              Record how a piece is made, certify it, bind it to an NFC or QR tag, and let anyone verify it in one
              tap — from the loom in Banaras to a collector's wardrobe.
            </p>
            <div className="s-ctas">
              <Link className="s-btn s-btn--primary" to="/studio/signup">Start free studio — 7 days, every module</Link>
              <a className="s-btn s-btn--ghost" href="#verify">Verify a piece</a>
            </div>
          </div>
          <div className="s-proof" aria-hidden="true">
            <div className="s-qr">
              <small>QR LABEL · SAMPLE</small>
              <QrMark />
            </div>
            <article className="s-pcard">
              <img src={brocadeImg} alt="" />
              <div className="s-pcard__body">
                <div className="s-pcard__row">
                  <span className="s-mono" style={{ fontSize: 12, color: "var(--ink-3)" }}>SAMPLE CERTIFICATE</span>
                  <span className="s-badge"><i />Verified</span>
                </div>
                <h4>Kadhua Banarasi, pure katan silk</h4>
                <span className="s-pcard__meta">Handwoven · pit loom</span>
                <dl className="s-kv">
                  <div><dt>Maker</dt><dd>Studio, Varanasi</dd></div>
                  <div><dt>GI</dt><dd>Banaras Brocades</dd></div>
                  <div><dt>Technique</dt><dd>Kadhua</dd></div>
                  <div><dt>Process</dt><dd>6 / 6 stages</dd></div>
                </dl>
                <div className="s-steps"><span /><span /><span /><span /><span /><span /></div>
              </div>
            </article>
            <div className="s-nfc">
              <Ripple />
              <small>NFC tag bound</small>
              <p>Tap to verify</p>
            </div>
          </div>
        </div>
      </section>

      <div className="s-facts">
        <div className="s-wrap s-facts__grid">
          {FACTS.map(([big, small]) => (
            <div className="s-fact" key={big}><b>{big}</b><span>{small}</span></div>
          ))}
        </div>
      </div>

      <section className="s-section s-reveal">
        <div className="s-wrap">
          <div className="s-head">
            <div>
              <span className="s-eyebrow">How proof works</span>
              <h2>From the loom to the living room — one unbroken record.</h2>
            </div>
            <p className="s-lead">
              Each step writes to the same piece record, so the certificate a buyer sees is the work itself, not a
              brochure.
            </p>
          </div>
          <div className="s-flow">
            <div className="s-thread" />
            {STAGES.map(([title, body, mod], i) => (
              <div className={`s-stage${i === 2 ? " s-stage--on" : ""}`} key={title}>
                <div className="s-stage__dot">{String(i + 1).padStart(2, "0")}</div>
                <h3>{title}</h3>
                <p>{body}</p>
                <span className="s-stage__mod">{mod}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="s-section s-section--tight s-reveal">
        <div className="s-wrap">
          <div className="s-head">
            <div>
              <span className="s-eyebrow">The platform</span>
              <h2>Twelve modules. One record of truth.</h2>
            </div>
            <p className="s-lead">
              Studio core is always on. Turn on what your studio needs — every module is free for the first 7 days.
            </p>
          </div>
          <ul className="s-mods" aria-label="Modules">
            {MODULES_COMPACT.map(([tag, name]) => (
              <li key={name}><small>{tag}</small>{name}</li>
            ))}
            <li className="s-mods__wide"><small>For AI agents</small>HKP inside Claude — run your studio from a chat</li>
          </ul>
          <div className="s-bento">
            <div className="s-tile s-span4 s-tall s-tile--with-ui">
              <span className="s-tile__tag">Studio core · always on</span>
              <h3>Product records &amp; certificates</h3>
              <p>
                Identity, process media, materials and provenance live with the piece. Issue a certificate buyers can
                check from any phone.
              </p>
              <div className="s-ui" aria-hidden="true">
                <div className="s-ui__dots"><i /><i /><i /></div>
                <table>
                  <thead>
                    <tr><th>Piece</th><th>Craft</th><th>Process</th><th>Tag</th><th>Status</th></tr>
                  </thead>
                  <tbody>
                    {PIECES.map(([name, craft, process, tag, status]) => (
                      <tr key={name}>
                        <td>{name}</td><td>{craft}</td><td>{process}</td><td className="s-mono">{tag}</td>
                        <td><span className={`s-pill ${STATUS[status][0]}`}>{STATUS[status][1]}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="s-tile s-span2 s-tall s-tile--dark s-tile--with-rings">
              <span className="s-tile__tag">Tags</span>
              <h3>NFC &amp; QR labels</h3>
              <p>Pre-printed QR per piece, studio allotment, NFC bound and frozen once issued.</p>
              <svg className="s-rings" width="233" height="233" viewBox="0 0 233 233" aria-hidden="true">
                <g fill="none" stroke="#5ac8fa">
                  <circle cx="116" cy="116" r="34" opacity=".9" />
                  <circle cx="116" cy="116" r="55" opacity=".5" />
                  <circle cx="116" cy="116" r="89" opacity=".22" />
                </g>
                <rect x="96" y="96" width="40" height="40" rx="9" fill="#0071e3" />
                <path d="M108 116h16M116 108v16" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </div>
            <div className="s-tile s-span2">
              <span className="s-tile__glyph" aria-hidden="true">⌗</span>
              <span className="s-tile__tag">Sell</span>
              <h3>Catalogue</h3>
              <p>Share with password, view limits and expiry — open, exclusive or reseller per link.</p>
            </div>
            <div className="s-tile s-span2">
              <span className="s-tile__glyph" aria-hidden="true">⫴</span>
              <span className="s-tile__tag">Run</span>
              <h3>Karkhana</h3>
              <p>Looms, daily work, attendance, rate cards, advances and wages.</p>
            </div>
            <div className="s-tile s-span2">
              <span className="s-tile__glyph" aria-hidden="true">⇄</span>
              <span className="s-tile__tag">Sell</span>
              <h3>Orders &amp; partners</h3>
              <p>Studio orders, field capture links, designer ↔ partner production.</p>
            </div>
            <div className="s-tile s-span2">
              <span className="s-tile__glyph" aria-hidden="true">⌖</span>
              <span className="s-tile__tag">Prove</span>
              <h3>Geo, GI &amp; looms</h3>
              <p>GI fences, loom registry and organisation certifications.</p>
            </div>
            <div className="s-tile s-span2">
              <span className="s-tile__glyph" aria-hidden="true">₹</span>
              <span className="s-tile__tag">Run</span>
              <h3>Saree costing</h3>
              <p>Per-piece costing with approval that flows into product prices.</p>
            </div>
            <div className="s-tile s-span2 s-tile--blue">
              <span className="s-tile__glyph" aria-hidden="true">✦</span>
              <span className="s-tile__tag">AI</span>
              <h3>AI enrichment</h3>
              <p>Vision-assisted descriptions, craft tags and stories from your photos.</p>
            </div>
            <div className="s-tile s-span3">
              <span className="s-tile__glyph" aria-hidden="true">♡</span>
              <span className="s-tile__tag">Keep</span>
              <h3>My Collection &amp; Care</h3>
              <p>
                Collectors save tagged pieces, add ones they already own, see ownership history and wash-care
                guidance.
              </p>
            </div>
            <div className="s-tile s-span3 s-tile--dark s-tile--with-code">
              <span className="s-tile__tag">For AI agents</span>
              <h3>HKP inside Claude</h3>
              <p>Our MCP server lets studios run orders, stock and catalogues from a chat — always with a confirm step.</p>
              <div className="s-code" aria-hidden="true">
                <b>create_order_draft</b>(customer, product, qty)
                <br />
                <u>✓ draft ready</u> — confirm_order?
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="s-section s-reveal">
        <div className="s-wrap">
          <div className="s-head">
            <div>
              <span className="s-eyebrow">Who it's for</span>
              <h2>Built for the whole chain of trust.</h2>
            </div>
            <p className="s-lead">
              The same record serves the person at the loom, the brand that sells it, and the family that keeps it for
              decades.
            </p>
          </div>
          <div className="s-aud">
            <article className="s-acard">
              <img src={loomImg} alt="A weaver's hands at a handloom" loading="lazy" />
              <div className="s-acard__b">
                <span className="s-eyebrow">Makers &amp; studios</span>
                <h3 style={{ fontSize: "var(--t3)" }}>Weavers, master weavers, designers, manufacturers</h3>
                <ul>
                  <li>Certify every piece you make</li>
                  <li>Run the karkhana floor without spreadsheets</li>
                  <li>Share catalogues you control</li>
                </ul>
                <Link className="s-more" to="/studio">Explore Studio →</Link>
              </div>
            </article>
            <article className="s-acard">
              <img src={collectorImg} alt="A woman wearing a handwoven saree" loading="lazy" style={{ objectPosition: "70% center" }} />
              <div className="s-acard__b">
                <span className="s-eyebrow">Collectors</span>
                <h3>People who buy and keep</h3>
                <ul>
                  <li>Tap to verify, no app</li>
                  <li>Keep certificates and care notes</li>
                  <li>Free app · QR tags from {formatUsd(TAG_PRICES_INR.qr, inrPerUsd)}</li>
                </ul>
                {collectorOn ? (
                  <Link className="s-more" to="/collectors">For collectors →</Link>
                ) : (
                  <a className="s-more" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Ask about tags →</a>
                )}
              </div>
            </article>
            <article className="s-acard">
              <img src={zariImg} alt="Hands finishing a zari border by needle" loading="lazy" />
              <div className="s-acard__b">
                <span className="s-eyebrow">Institutions</span>
                <h3>GI bodies, councils, brands</h3>
                <ul>
                  <li>GI-region traceability</li>
                  <li>Data on artisan livelihoods</li>
                </ul>
                <a className="s-more" href={SUPPORT_WHATSAPP} target="_blank" rel="noreferrer">Talk to us →</a>
              </div>
            </article>
          </div>
        </div>
      </section>

      <section className="s-section s-verify" id="verify">
        <div className="s-wrap s-verify__grid">
          <div>
            <span className="s-eyebrow">Verify</span>
            <h2 style={{ marginTop: "var(--s2)" }}>Holding a tagged piece? Check it in seconds.</h2>
            <p className="s-lead" style={{ marginTop: "var(--s3)" }}>
              Tap the NFC tag with your phone, scan the QR label, or paste the link printed with it. You'll see who
              made it, how, and where.
            </p>
          </div>
          <VerifyBox />
        </div>
      </section>

      <section className="s-final s-reveal">
        <div className="s-wrap">
          <span className="s-eyebrow">Start today</span>
          <h2>Give your craft the proof it deserves.</h2>
          <p className="s-lead">Every Studio module free for 7 days. Keep only what you need.</p>
          <div className="s-ctas">
            <Link className="s-btn s-btn--primary" to="/studio/signup">Start free studio</Link>
            <Link className="s-btn s-btn--ghost" to="/pricing">See pricing</Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default HomePage;
